import {Overlay} from "@angular/cdk/overlay"
import {SlicePipe} from "@angular/common"
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Injector,
    OnDestroy,
    Signal,
    signal,
    viewChild,
} from "@angular/core"
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {MatDividerModule} from "@angular/material/divider"
import {MatMenuModule} from "@angular/material/menu"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {ActivatedRoute, Router} from "@angular/router"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {HintComponent, HintTypes} from "@app/common/components/hint/hint.component"
import {JobTaskProgressComponent} from "@common/components/job-task-progress/job-task-progress.component"
import {DialogSize} from "@app/common/models/dialogs"
import {OperatorInspectorComponent} from "@app/textures/texture-editor/operators-panel/operator-inspector/operator-inspector.component"
import {byOperatorType} from "@app/textures/texture-editor/operators-panel/operator-desc"
import {OperatorsPanelComponent} from "@app/textures/texture-editor/operators-panel/operators-panel.component"
import {TextureSelectorComponent} from "@app/textures/texture-editor/texture-selector/texture-selector.component"
import {TilingCanvasComponent} from "@app/textures/texture-editor/tiling-canvas/tiling-canvas.component"
import {TilingControlsComponent} from "@app/textures/texture-editor/tiling-controls/tiling-controls.component"
import {TilingInfoPanelComponent} from "@app/textures/texture-editor/tiling-info-panel/tiling-info-panel.component"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {Size2} from "@cm/math"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {AuthService} from "@common/services/auth/auth.service"
import {HotkeyLayerId, Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {ImageDataType, TextureType} from "@generated"
import {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"
import {TextureEditProcessing} from "@app/textures/texture-editor/texture-edit-processing"
import {TextureRevisionActionsService} from "@app/textures/texture-editor/texture-revision-actions.service"
import {TextureRevisionLoaderService} from "@app/textures/texture-editor/texture-revision-loader.service"
import {TextureEditorCallback} from "@app/textures/texture-editor/texture-editor-callback"
import {TextureEditorDataService} from "@app/textures/texture-editor/texture-editor-data.service"
import {debounceTime, filter, interval} from "rxjs"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {MatTooltip} from "@angular/material/tooltip"
import {CM_LOGGER_TEXTURES} from "@app/common/services/logging"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-editor",
    templateUrl: "./texture-editor.component.html",
    styleUrls: ["./texture-editor.component.scss"],
    providers: [TextureEditProcessing, TextureRevisionActionsService, TextureRevisionLoaderService, TextureEditorDataService],
    imports: [
        HintComponent,
        JobTaskProgressComponent,
        MatMenuModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        OperatorsPanelComponent,
        OperatorInspectorComponent,
        RoutedDialogComponent,
        TextureSelectorComponent,
        TilingCanvasComponent,
        TilingControlsComponent,
        TilingInfoPanelComponent,
        ButtonComponent,
        SlicePipe,
        MatTooltip,
    ],
})
export class TextureEditorComponent implements AfterViewInit, OnDestroy, TextureEditorCallback {
    private readonly logger = inject(CM_LOGGER_TEXTURES)
    readonly $overlayDialog = viewChild.required<RoutedDialogComponent>("overlayDialog")
    readonly $tilingCanvas = viewChild.required<TilingCanvasComponent>("tilingCanvasRef")

    DialogSize = DialogSize
    HintTypes = HintTypes

    readonly $isDebugEnabled = signal(TextureEditorSettings.EnableDebug)

    private readonly hotkeyLayerId: HotkeyLayerId

    private readonly destroyRef = inject(DestroyRef)
    readonly overlay = inject(Overlay)
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)
    private readonly dialog = inject(MatDialog)
    private readonly authService = inject(AuthService)
    private readonly hotkeys = inject(Hotkeys)
    private readonly injector = inject(Injector)
    protected readonly actions = inject(TextureRevisionActionsService)
    protected readonly loader = inject(TextureRevisionLoaderService)
    protected readonly dataService = inject(TextureEditorDataService)

    constructor() {
        // register this.hotkeys
        this.hotkeyLayerId = this.hotkeys.createLayer() // create a layer since this is a modal dialog
        this.hotkeys.addShortcuts({
            bindings: [
                {
                    keys: ["control.shift.d"],
                    guard: () => this.authService.isStaff(),
                    action: () => {
                        this.$isDebugEnabled.update((value) => !value)
                        this.logger.info(this.$isDebugEnabled() ? "DEBUG MODE ENABLED" : "DEBUG MODE DISABLED")
                    },
                },
                {keys: ["d"], description: "Previous revision", action: () => this.previousRevision()},
                {keys: ["f"], description: "Next revision", action: () => this.nextRevision()},
                {
                    keys: "g",
                    description: "Show grid + outline",
                    action: () => {
                        if (!this.$tilingCanvas().$gridActive()) this.$tilingCanvas().toggleGrid()
                        this.$tilingCanvas().toggleGridOutline()
                    },
                },
                {keys: "t", description: "Toggle grid [T]", action: () => this.$tilingCanvas().toggleGrid()},
                {keys: "b", description: "Boost dark regions in display [B]", action: () => this.$tilingCanvas().toggleBrightnessBoost()},
                {keys: "Escape", action: () => this.$overlayDialog().closeIfClean()},
                {
                    keys: ["control.s", "meta.s"],
                    description: "Save",
                    guard: () => this.$canSubmit() && this.$isEdited(),
                    action: () => void this.save(),
                },
            ],
            takeUntilDestroy: takeUntilDestroyed(this.destroyRef),
        })
    }

    ngAfterViewInit(): void {
        const operatorStack = new OperatorStack(this, this.injector, this.$tilingCanvas())
        this.$_operatorStack.set(operatorStack)
        operatorStack.resultImageChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((image) => this.onResultImageChanged(image))
        effect(
            () => {
                const display = this.dataService.$displayDataObject()
                if (!display) {
                    return
                }
                this.$tilingCanvas().$isSrgb.set(display.dataObject.imageDataType === ImageDataType.Color)
                this.$tilingCanvas().$physicalInfo.update((current) => ({...current, pixelsPerCm: this.dataService.$pxPerCm()?.width ?? 1}))
                this._tilingCanvasShowsResultDataObject = display.isResult
            },
            {injector: this.injector},
        )
        this.route.url.subscribe(() => this.loader.changeTexture(this))
        this.route.queryParamMap.subscribe(() => this.loader.changeTexture(this))
        interval(3000)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => !document.hidden),
            )
            .subscribe(() => this.checkForProcessingEdits())
    }

    // passed as TilingCanvasComponent's onAboutToBeDestroyed input, awaited before it disposes the WebGL2 context
    protected readonly disposeOperatorStack = async (): Promise<void> => {
        await this.$_operatorStack()?.dispose()
        this.$_operatorStack.set(undefined)
        this.hotkeys.removeLayer(this.hotkeyLayerId)
    }

    ngOnDestroy(): void {}

    get isDebugEnabled(): boolean {
        return this.$isDebugEnabled()
    }

    get imageIsUpToDate(): boolean {
        return !this._tilingCanvasShowsResultDataObject || this.resultDataObjectUpToDate
    }

    protected async onFrameDrawn(): Promise<void> {
        if (this._handledFirstFrame) {
            return
        }
        this._handledFirstFrame = true
        try {
            if (!this._cacheBackfillAttemptedForRevision) {
                this._cacheBackfillAttemptedForRevision = true
                await this.actions.maybePersistCacheBackfill(this)
            }
        } finally {
            this.$_loadingComplete.set(true)
        }
    }

    protected onCanvasLoadError(error: Error): void {
        this.$_canvasLoadError.set(error)
    }

    private onResultImageChanged(image: AnyImageOpHal | null) {
        this.$_canvasLoadError.set(null)
        this.$resultResolution.set(image ? new Size2(image.descriptor.width, image.descriptor.height) : new Size2(0, 0))
        this.$tilingCanvas().setResultImage(image)
        this.$tilingCanvas().setMessage(
            this.imageIsUpToDate ? undefined : "Edits have been made and HQ processing has not completed yet so this image might be outdated.",
        )
    }

    checkForProcessingEdits() {
        return this.loader.checkForProcessingEdits()
    }

    get enableDebugFeatures(): boolean {
        return this.authService.isSuperuser()
    }

    readonly $isBusy = computed(() => this.$_busyCounter() > 0 || (this.$_operatorStack()?.$isBusy() ?? false))
    readonly $showSpinner = toSignal(toObservable(this.$isBusy).pipe(debounceTime(200)), {initialValue: false})

    readonly $isInitialFrameDrawn = computed(() => this.$_loadingComplete())

    readonly $resultImageError = computed(() => this.$_operatorStack()?.resultImageError ?? this.$_canvasLoadError())
    // Only a fatal "revision failed to load" before any frame has ever been drawn. Errors during
    // normal editing after a successful initial load keep today's toast-only behavior.
    readonly $initialLoadError = computed(() => (!this.$isInitialFrameDrawn() ? this.$resultImageError() : null))

    readonly $isDataValid = computed(
        () =>
            !!this.dataService.$revision() &&
            !this.$initialLoadError() &&
            this.dataService.$hasValidResolutions() &&
            this.dataService.$hasValidRatio() &&
            !this.dataService.$isLegacyUnsupportedEdit(),
    )

    readonly $canEdit = computed(() => this.$isDataValid() && !this.dataService.$isInUse() && this.dataService.$canEditDisplacementMap())

    readonly $hasOperators = computed(() => {
        const operatorStack = this.$_operatorStack()
        return !!operatorStack && !!operatorStack.operators.find((operator) => !operator.flags.has("is-internal"))
    })

    readonly $isEdited = computed(() => this.$_isEdited() || (this.$_operatorStack()?.$isEdited() ?? false))

    readonly $isOperatorStackValid = computed(() => this.$_operatorStack()?.$isValid() ?? true)

    // Checks $isInUse rather than $canEdit: edits to another map must stay submittable while the auto-generated displacement map is selected
    readonly $canSubmit = computed(() => this.$isOperatorStackValid() && !this.dataService.$isInUse())

    readonly $operatorValidationMessages = computed(() =>
        (this.$_operatorStack()?.operators ?? []).filter((op) => !op.$valid()).map((op) => `${byOperatorType[op.type].label}: ${op.$validationMessage()}`),
    )

    get resultDataObjectUpToDate(): boolean {
        const resultDataObject = this.dataService.$textureTypeData()?.resultDataObject
        return !!resultDataObject && !this.$isEdited() && !this.dataService.$isProcessingEdits()
    }

    private previousRevision(): void {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No selected texture revision info.")
        }
        const revisions = revision.textureSet.textureSetRevisions
        const currentIndex = revisions.findIndex((r) => r.id === revision.textureSetRevision.id)
        if (currentIndex < revisions.length - 1) {
            this.setTextureSetRevision(revisions[currentIndex + 1].id)
        }
    }

    private nextRevision(): void {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No selected texture revision info.")
        }
        const revisions = revision.textureSet.textureSetRevisions
        const currentIndex = revisions.findIndex((r) => r.id === revision.textureSetRevision.id)
        if (currentIndex > 0) {
            this.setTextureSetRevision(revisions[currentIndex - 1].id)
        }
    }

    // zoomTo(percentage: number): void {
    //     this.$tilingCanvas().$canvasBase().navigation.zoomTo(percentage)
    // }

    protected copyCurrentRevision(copyOperators: boolean) {
        return this.actions.copyCurrentRevision(this, copyOperators)
    }

    protected importOperatorsFromRevision() {
        return this.actions.importOperatorsFromRevision(this)
    }

    protected deleteCurrentRevision() {
        return this.actions.deleteCurrentRevision(this)
    }

    protected save() {
        return this.actions.save(this)
    }

    protected discardChanges() {
        return this.actions.discardChanges(this)
    }

    // protected downloadGraph(): void {
    //     this.$_operatorStack()?.downloadOperatorNodeGraph()
    // }

    readonly $hasOperatorStack = computed(() => this.$_operatorStack() != null)

    protected readonly $operatorStackOrThrow = computed(() => {
        const stack = this.$_operatorStack()
        if (!stack) {
            throw Error("No operator stack set.")
        }
        return stack
    })

    readonly $selectedOperator = computed(() => this.$_operatorStack()?.selectedOperator ?? null)

    readonly $inspectedOperator = computed(() => {
        const operator = this.$selectedOperator()
        // The canvas shows the baked result here and operators aren't evaluated, so there is nothing to inspect
        if (!operator || this.dataService.$displayDataObject()?.isResult) return null
        // A locked operator can't be unlocked while in use, so it has to stay inspectable
        return !operator.$locked() || this.dataService.$isInUse() ? operator : null
    })

    setTextureSetRevision(textureSetRevisionId: string, options?: {warnIfEdited?: boolean; forceReload?: boolean}) {
        const warnIfEdited = options?.warnIfEdited ?? true
        const forceReload = options?.forceReload ?? false
        if (!forceReload && this.dataService.$revision()?.textureSetRevision.id === textureSetRevisionId) {
            return
        }
        const navToRevision = (textureSetRevisionId: string) => {
            return this.navigateToTextureSetRevision(textureSetRevisionId, this.dataService.$selectedTextureType())
        }
        if (!this.$isEdited() || !warnIfEdited) {
            void navToRevision(textureSetRevisionId)
        } else {
            const dialogRef = this.dialog.open(DialogComponent, {
                disableClose: false,
                width: "400px",
                data: {
                    title: "Switch texture revision",
                    message:
                        "You are attempting to switch the texture revision which will cause your <strong>unsaved changes to be lost</strong>.<br><br>Are you sure you want to continue ?",
                    confirmLabel: "Switch revision and lose changes",
                    cancelLabel: "Cancel",
                },
            })
            dialogRef
                .afterClosed()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((confirmed) => {
                    if (confirmed) {
                        navToRevision(textureSetRevisionId)
                    }
                })
        }
    }

    setTextureType(textureType: TextureType) {
        const revision = this.dataService.$revision()
        if (!revision) {
            throw Error("No selected texture revision info.")
        }
        this.navigateToTextureSetRevision(revision.textureSetRevision.id, textureType)
    }

    private async navigateToTextureSetRevision(textureSetRevisionId: string, textureType: TextureType | undefined): Promise<void> {
        return this.router
            .navigate(["../", textureSetRevisionId], {
                relativeTo: this.route,
                queryParamsHandling: "merge",
                queryParams: {textureType},
            })
            .then((result) => {
                // In case of switching between a newly tiled texture and another texture version, the route URL does not change so this has to be triggered manually.
                if (result == null) {
                    this.loader.changeTexture(this)
                }
            })
    }

    // TODO migrate to GQL api
    // private setupMaterialGraphFromTextureSet(textureSet: TextureSet): Observable<MaterialGraph> {
    //     const set0 = new Set(MATERIAL_TEMPLATE_0_TEXTURE_TYPES)
    //     const set1 = new Set(MATERIAL_TEMPLATE_1_TEXTURE_TYPES)
    //     let dimensionRange: {maxWidth: number; minWidth: number; maxHeight: number; minHeight: number}
    //     const typeToRevision = new Map<string, number>()
    //     // TODO: This can be removed at some point. Only required because there was a rounding error in the displacement map calculation.
    //     const checkTextures = new Set([...MATERIAL_TEMPLATE_0_TEXTURE_TYPES, ...MATERIAL_TEMPLATE_1_TEXTURE_TYPES])
    //     textureSet.textures.map((texture) => {
    //         if (!checkTextures.has(texture.type)) return
    //         if (set0.has(texture.type)) set0.delete(texture.type)
    //         if (set1.has(texture.type)) set1.delete(texture.type)

    //         const revision = texture.getLatestRevision()
    //         if (!dimensionRange) {
    //             dimensionRange = {
    //                 maxWidth: revision.width,
    //                 minWidth: revision.width,
    //                 maxHeight: revision.height,
    //                 minHeight: revision.height,
    //             }
    //         } else {
    //             dimensionRange.maxWidth = Math.max(dimensionRange.maxWidth, revision.width)
    //             dimensionRange.minWidth = Math.min(dimensionRange.minWidth, revision.width)
    //             dimensionRange.maxHeight = Math.max(dimensionRange.maxHeight, revision.height)
    //             dimensionRange.minHeight = Math.min(dimensionRange.minHeight, revision.height)
    //         }

    //         typeToRevision.set(texture.type.label, revision.id)
    //     })
    //     if (
    //         Math.abs(dimensionRange.maxWidth - dimensionRange.minWidth) > ALLOWED_MAP_DIMENSION_VARIATION_IN_CM ||
    //         Math.abs(dimensionRange.maxHeight - dimensionRange.minHeight) > ALLOWED_MAP_DIMENSION_VARIATION_IN_CM
    //     ) {
    //         throw new Error("Non matching physical sizes for texture revisions")
    //     }
    //     if (set0.size !== 0 && set1.size !== 0) throw new Error("Not all required texture types present")

    //     const templateRevisionId = set0.size === 0 ? Settings.MATERIAL_TEMPLATE_0_MATERIAL_REVISION_ID : Settings.MATERIAL_TEMPLATE_1_MATERIAL_REVISION_ID
    //     let queryParams: HttpParams = new HttpParams()
    //     queryParams = queryParams.set("material_revision", String(templateRevisionId))

    //     const dimensions = [(dimensionRange.maxWidth + dimensionRange.minWidth) * 0.5, (dimensionRange.maxHeight + dimensionRange.minHeight) * 0.5]
    //     const nodesOp = MaterialNode.getAll(queryParams).pipe(
    //         switchMap((templateMaterialNodes) =>
    //             forkJoinZeroOrMore([observableOf(templateMaterialNodes), forkJoin(this._textureRevisionToType(templateMaterialNodes))])
    //         ),
    //         map(([materialNodes, templateRevisionToType]) => {
    //             materialNodes = materialNodes.map((node) => {
    //                 if (node.name === "Mapping") {
    //                     const idx = node.parameters.findIndex((p) => p.name === "Scale")
    //                     node.parameters[idx].value = [...dimensions, 1.0]
    //                 } else if (node.name === "TexImage") {
    //                     node.textureRevision = typeToRevision.get(templateRevisionToType[node.textureRevision])
    //                 }
    //                 return node
    //             })
    //             this.nodes = materialNodes
    //         })
    //     )

    //     const connectionsOp = MaterialConnection.getAll(queryParams).pipe(
    //         map((materialConnections) => {
    //             this.connections = materialConnections
    //         })
    //     )

    //     return forkJoinZeroOrMore([nodesOp, connectionsOp]).pipe(
    //         switchMap(() =>
    //             MaterialGraph.fromNodesAndConnections(this.nodes, this.connections, activeRevisionId, this.material.id, this.material.name).pipe(
    //                 map((graph) => {
    //                     this.materialGraph = graph
    //                     if (updateNodesAndConnections) {
    //                         this.nodeEditor.updateNodesAndConnections(this.nodes)
    //                     }
    //                 })
    //             )
    //         )
    //     )
    // }

    setBusy(busy: boolean) {
        if (busy) {
            this.busyInc()
        } else {
            this.busyDec()
        }
    }

    private busyInc() {
        this.$_busyCounter.update((value) => value + 1)
    }

    private busyDec() {
        this.$_busyCounter.update((value) => value - 1)
    }

    private readonly $_busyCounter = signal(0)
    private readonly $_operatorStack = signal<OperatorStack | undefined>(undefined)
    private readonly $_isEdited = signal(false)
    private readonly $_loadingComplete = signal(false)
    private readonly $_canvasLoadError = signal<Error | null>(null)
    private _tilingCanvasShowsResultDataObject = false
    private _handledFirstFrame = false
    private _cacheBackfillAttemptedForRevision = false

    readonly $resultResolution = signal(new Size2(0, 0), {equal: (a, b) => a.width === b.width && a.height === b.height})
    readonly $operatorStack: Signal<OperatorStack | undefined> = this.$_operatorStack.asReadonly()

    protected TextureType = TextureType
    readonly $getRevisionTitle = signal(getRevisionTitle)

    navigateBack() {
        void this.$overlayDialog().closeIfClean()
    }

    closeDialogIfClean(): void {
        void this.$overlayDialog().closeIfClean()
    }

    resetForNewLoad(textureSetRevisionChanged: boolean): void {
        this.$_isEdited.set(false)
        this.$_loadingComplete.set(false)
        this.$_canvasLoadError.set(null)
        this._handledFirstFrame = false
        if (textureSetRevisionChanged) {
            this._cacheBackfillAttemptedForRevision = false
        }
    }
}
